import { useCallback, useEffect, useRef, useState } from "react";

type LeaveAction = () => void;

const isPlainClick = (event: MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

// An in-app link that would take the rep off this page, or null.
const leavingAnchor = (event: MouseEvent) => {
  if (event.defaultPrevented || !isPlainClick(event)) return null;
  const anchor = (event.target as Element | null)?.closest?.("a[href]");
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.hasAttribute("download")) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  return anchor;
};

// The app runs on a non-data router, so there is no useBlocker: in-app links
// are caught at the document before the router's own handler sees the click,
// and reload or tab close gets the browser's prompt. Browser back and
// programmatic navigation are not caught.
export const useLeaveGuard = (dirty: boolean) => {
  const [pending, setPending] = useState<LeaveAction>();
  const bypass = useRef(false);
  // Set once a discard reloads the page, so its unload is not prompted.
  const unloading = useRef(false);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (unloading.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const onClick = (event: MouseEvent) => {
      if (bypass.current) return;
      const anchor = leavingAnchor(event);
      if (!anchor) return;
      event.preventDefault();
      event.stopPropagation();
      setPending(() => () => {
        if (anchor.isConnected) {
          anchor.click();
        } else {
          unloading.current = true;
          window.location.assign(anchor.href);
        }
      });
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);

  const requestLeave = useCallback(
    (leave: LeaveAction) => {
      if (dirty) {
        setPending(() => leave);
      } else {
        leave();
      }
    },
    [dirty]
  );

  const discard = useCallback(() => {
    const leave = pending;
    setPending(undefined);
    if (!leave) return;
    bypass.current = true;
    try {
      leave();
    } finally {
      bypass.current = false;
    }
  }, [pending]);

  const stay = useCallback(() => setPending(undefined), []);

  return { requestLeave, leavePending: !!pending, discard, stay };
};
